// The client side of /api/series (BUILD-BRIEF-R6 §2.5) — the Market page's Series zone and the
// Organisation page's Named-in-sources zone read the store through this file only.
//
// `routes/series.ts` is a sibling package's file (§2.5's own header names it `w2-routes`); the
// shapes below match what it actually answers (read alongside this file, 2026-09-05) rather than
// a guess — a plain `series_points` row per item, `GET /catalogue` grouping by (series, source)
// with one entry per source under `sources[]`.
//
// Types come from `src/shared/series.v1.ts` — NOT from `shared/types.ts` (§7's deviation #1,
// the planner's note): `SeriesPoint`, `SeriesGeoLevel`, `SeriesPeriodKind`, `SeriesUnit` all live
// beside the vocabulary they belong to; `seriesLabel`/`dimLabel`/`DIMENSIONS` render the record's
// own words, not an i18n key.
import { qs, request } from './client.js';
import type {
  SeriesGeoLevel, SeriesPeriodKind, SeriesPoint, SeriesUnit,
} from '../../shared/series.v1.js';

export interface SeriesFilters {
  series?: string;
  /** Defaults, server-side, to the series' own `source_key` (§2.5) — omit for the primary. */
  source?: string;
  geo_level?: SeriesGeoLevel;
  geo_code?: string;
  /** One value per dimension the series declares; sent as `dims.<k>=<v>` query params. */
  dims?: Record<string, string>;
  /** Adds superseded points alongside current ones — the pair a cell's evidence toggle needs to
   *  say "was {value} in the file of {date}" (§2.4). */
  history?: boolean;
}

export interface SeriesGeography {
  geo_level: SeriesGeoLevel;
  geo_code: string;
  geo_name: string;
}

/** `GET /api/series` — plain `series_points` rows (current only, unless `history: true`),
 *  every period the store holds for this read, and the geographies (or, for a canton-only
 *  series, the geography rows the picker's own `dims`-as-rows fallback still needs) it touched. */
export interface SeriesListResponse {
  items: SeriesPoint[];
  total: number;
  periods: string[];
  geographies: SeriesGeography[];
}

/** `dims.<k>=` needs one query param per dimension — `qs` from `client.ts` only flattens a flat
 *  record, so the dims are folded in here rather than widening that shared helper for one caller. */
function seriesQs(filters: SeriesFilters): string {
  const { dims, ...rest } = filters;
  const flat: Record<string, string | number | boolean | null | undefined> = {
    ...rest, history: filters.history ? 1 : undefined,
  };
  if (dims) for (const [k, v] of Object.entries(dims)) if (v) flat[`dims.${k}`] = v;
  return qs(flat);
}

export function listSeries(filters: SeriesFilters = {}): Promise<SeriesListResponse> {
  return request(`/series${seriesQs(filters)}`);
}

export interface SeriesCatalogueSourceRow {
  source_key: string;
  primary: boolean;
  /** `null` — never `0` — for a (series, source) pair nothing has filled yet. */
  count: number | null;
  latest_period: string | null;
}

export interface SeriesCatalogueRow {
  key: string; en: string; de: string; fr: string;
  unit: SeriesUnit;
  geo_level: SeriesGeoLevel;
  period_kind: SeriesPeriodKind;
  dims: string[];
  definition: string;
  sources: SeriesCatalogueSourceRow[];
}

/** `GET /api/series/catalogue` — the whole `SERIES` vocabulary, one row per series, each with
 *  its primary and (when it has one) `also_from` sources' own counts (§2.5). */
export interface SeriesCatalogueResponse {
  items: SeriesCatalogueRow[];
  total: number;
}

export function seriesCatalogue(): Promise<SeriesCatalogueResponse> {
  return request('/series/catalogue');
}
